import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { RootStackParamList } from '../../types/navigation';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../styles/ThemeProvider';
import { useCommonStyles } from '../../styles/useCommonStyles';

type Props = NativeStackScreenProps<RootStackParamList, 'SignUp'>;

export default function SignUpScreen({ navigation }: Props) {
  const theme = useTheme();
  const commonStyles = useCommonStyles();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { signInWithGoogle, signInWithApple } = useAuth();

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    try {
      await signInWithGoogle();
      // OAuth will redirect, so no need to navigate here
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setLoading(true);
    setError('');
    try {
      await signInWithApple();
      // OAuth will redirect, so no need to navigate here
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <SafeAreaView style={commonStyles.safeArea}>
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <View style={[commonStyles.screenContainer, styles.formWrapper]}>
          <View style={commonStyles.form}>
            <Text style={commonStyles.screenTitle}>Create Account</Text>
            <Text style={commonStyles.screenSubtitle}>Choose how to sign up</Text>

            {error ? (
              <View style={commonStyles.errorContainer}>
                <Text style={commonStyles.errorText}>{error}</Text>
              </View>
            ) : null}

            <TouchableOpacity
              style={[commonStyles.socialButton, loading && commonStyles.disabledButton]}
              onPress={handleGoogleSignIn}
              disabled={loading}
            >
              <Ionicons name="logo-google" size={20} color="#DB4437" />
              <Text style={commonStyles.socialButtonText}>Continue with Google</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[commonStyles.socialButton, loading && commonStyles.disabledButton]}
              onPress={handleAppleSignIn}
              disabled={loading}
            >
              <Ionicons name="logo-apple" size={20} color={theme.colors.textPrimary} />
              <Text style={commonStyles.socialButtonText}>Continue with Apple</Text>
            </TouchableOpacity>

            <View style={commonStyles.separator}>
              <View style={commonStyles.separatorLine} />
              <Text style={commonStyles.separatorText}>or</Text>
              <View style={commonStyles.separatorLine} />
            </View>

            <TouchableOpacity
              style={[commonStyles.socialButton, loading && commonStyles.disabledButton]}
              onPress={() => navigation.navigate('EmailSignUp')}
              disabled={loading}
            >
              <Text style={commonStyles.socialButtonText}>Continue with Email</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={commonStyles.link}
              onPress={() => navigation.navigate('SignIn', { redirect: undefined })}
              disabled={loading}
            >
              <Text style={commonStyles.linkText}>Already have an account? Sign In</Text>
            </TouchableOpacity>

            {loading && (
              <View style={commonStyles.loadingOverlay} pointerEvents="auto">
                <ActivityIndicator size={36} color={theme.colors.accent} />
              </View>
            )}
          </View>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  flex: {
    flex: 1,
  },
  formWrapper: {
    flex: 1,
    justifyContent: 'center',
  },
});
